import {maybe} from '@freckle/maybe'
import {PATHS} from '@freckle/educator-entities/ts/common/helpers/paths'
import {toFragment} from '@freckle/educator-entities/ts/common/helpers/routers/query-params'
import type {TabKeyT} from '@freckle/school/ts/manage-teachers/components/manage-teachers/utils'

export const Routes = {
  redirectAuthenticated: () => '/redirect-authenticated',
  ssoLogoutToRenaissance: () => `${PATHS.unversionedAPIUrl}/3/renaissance/home`,
  growth: (schoolId?: number | null) =>
    schoolId !== undefined && schoolId !== null ? `/growth/${schoolId}` : '/growth',
  dashboard: (queryParams?: DashboardQueryParamsT) => {
    const baseUrl = '/dashboard'
    return buildDashboardQueryParams(baseUrl, queryParams)
  },
  reports: (schoolId: number) => `/reports/${schoolId}`,
  usage: (schoolId?: number) => (schoolId !== undefined ? `/usage/${schoolId}` : '/usage'),
  rostering: {
    importRoster: () => '/rostering/import-roster',
    cleanUpRoster: () => '/rostering/clean-up-roster',
    cleanUpRosterLink: () => '/rostering/clean-up-roster',
    removeSelfRosteredTeachers: (queryParams?: RemoveSelfRosteredTeachersQueryParamsT) => {
      const baseUrl = '/rostering/clean-up-roster/remove-self-rostered-teachers'
      return buildSelfRosteredTeachersQueryParams(baseUrl, queryParams)
    }
  },
  assessments: () => `/assessments`,
  schools: {
    assessments: (schoolId: number, year: number) =>
      toFragment(`/schools/${schoolId}/assessments`, {year}),
    assessment: (schoolId: number, assessmentId: number, teacherId: number) =>
      `/assessments/${schoolId}/${assessmentId}?teacher=${teacherId}`
  },
  schoolProficiencyReport: () => `/proficiency-report`,
  teacherProficiencyReport: (teacherId: number) => `/proficiency-report/teachers/${teacherId}`,
  settings: () => `/settings`,
  register: () => `/register`,
  registered: () => `/registered`,
  login: () => `/login`,
  forgotPassword: () => `/forgot-password`,
  setPassword: () => `/set-password`,
  noSchools: () => '/no-schools',
  errorPage: () => '/error'
}

type StudentFilterQueryParamTagT = 'student-filter-params'
export const STUDENT_FILTER_QUERY_PARAM_TAG: StudentFilterQueryParamTagT = 'student-filter-params'

type CourseFilterQueryParamTagT = 'course-filter-params'
export const COURSE_FILTER_QUERY_PARAM_TAG: CourseFilterQueryParamTagT = 'course-filter-params'

type TeacherFilterQueryParamTagT = 'teacher-filter-params'
export const TEACHER_FILTER_QUERY_PARAM_TAG: TeacherFilterQueryParamTagT = 'teacher-filter-params'

export type StudentTabFiltersQueryParamsT = {
  tag: StudentFilterQueryParamTagT
  courseId?: number | null
  courseName?: string | null
  teacherId?: number | null
  teacherName?: string | null
  grade?: number | null
  source?: 'admin' | 'non-admin' | null
  studentName?: string
  sisId?: string
  identityProviders?: Array<string>
}

export type CourseTabFilterQueryParamsT = {
  tag: CourseFilterQueryParamTagT
  sisIdExists?: boolean
  studentId?: number
  studentName?: string
  courseName?: string
  studentGrade?: string
  teacherName?: string
  identityProviders?: Array<string>
}

export type TeacherTabFilterQueryParamsT = {
  tag: TeacherFilterQueryParamTagT
  studentId?: number
  studentName?: string
  courseId?: number
  courseName?: string
  schoolName?: string
  identityProviders?: Array<string>
  role?: string | null
  studentGrade?: string
  teacherName?: string
  email?: string
}

export type AdditionalFiltersQueryParamsT =
  StudentTabFiltersQueryParamsT | CourseTabFilterQueryParamsT | TeacherTabFilterQueryParamsT

export type DashboardQueryParamsT = {
  schoolIds?: Array<number> | null
  activeTab?: TabKeyT | null
  position?: string | null
  searchValue?: string | null
  limit?: number | null
  additionalFilters?: AdditionalFiltersQueryParamsT | null
}

function buildDashboardQueryParams(
  baseUrl: string,
  queryParams?: DashboardQueryParamsT | null
): string {
  if (queryParams === null || queryParams === undefined) {
    return baseUrl
  }

  const {schoolIds, activeTab, searchValue, limit, position, additionalFilters} = queryParams

  const schoolIdsAttr =
    schoolIds !== null && schoolIds !== undefined && schoolIds.length > 0 ? {schoolIds} : {}

  const activeTabAttr = activeTab !== null && activeTab !== undefined ? {activeTab} : {}

  const searchValueQueryAttr =
    searchValue !== null && searchValue !== undefined && searchValue.length > 0 ? {searchValue} : {}

  const limitQueryAttr = maybe(
    () => ({}),
    s => ({limit: s.toString()}),
    limit
  )
  const positionQueryAttr = maybe(
    () => ({}),
    p => ({position: p}),
    position
  )

  return toFragment(baseUrl, {
    ...schoolIdsAttr,
    ...activeTabAttr,
    ...searchValueQueryAttr,
    ...limitQueryAttr,
    ...positionQueryAttr,
    ...additionalFilters
  })
}

type RemoveSelfRosteredTeachersQueryParamsT = {
  schoolIds?: Array<number> | null
  position?: string | null
  searchValue?: string | null
  limit?: number | null
}

function buildSelfRosteredTeachersQueryParams(
  baseUrl: string,
  queryParams?: RemoveSelfRosteredTeachersQueryParamsT | null
): string {
  if (queryParams === null || queryParams === undefined) {
    return baseUrl
  }

  const {schoolIds, limit, searchValue, position} = queryParams

  const schoolIdsAttr =
    schoolIds !== null && schoolIds !== undefined && schoolIds.length > 0 ? {schoolIds} : {}

  const limitQueryAttr = maybe(
    () => ({}),
    s => ({limit: s.toString()}),
    limit
  )

  const searchValueQueryAttr =
    searchValue !== null && searchValue !== undefined && searchValue.length > 0 ? {searchValue} : {}

  const positionQueryAttr = maybe(
    () => ({}),
    p => ({position: p}),
    position
  )

  return toFragment(baseUrl, {
    ...schoolIdsAttr,
    ...limitQueryAttr,
    ...searchValueQueryAttr,
    ...positionQueryAttr
  })
}
